<template>
  <BittsQuickFilters
    class="partner-tab-actions"
    :filter-options="filterOptions"
    :filter-value-options="filterValueOptions"
    :filter-text-overrides="populationChipTexts"
    @update-filters="handleUpdateFilters"
    @cleared-filter="resetSelection"
  >
    <template #prefix-controls>
      <slot name="segment" />
    </template>
    <!-- Renders nothing for the other filters, which then fall back to their
         own option lists. -->
    <template #content="{ filter, setFilterValue }">
      <PartnerTabPopulationFilter
        v-if="isPopulationFilter(filter.value)"
        :side="POPULATION_SIDES[filter.value]"
        :partner-org-ids="partnerOrgIds"
        :selection="selections[filter.value]"
        @update:selection="
          updateSelection(filter.value, $event, setFilterValue)
        "
      />
    </template>
    <template #controls>
      <div class="flex items-center gap-6">
        <BittsInput
          v-model="searchValue"
          type="search"
          name="partner-search"
          placeholder="Search partners"
          size="small"
          @update:model-value="debouncedSearch"
        />
        <slot name="actions" />
      </div>
    </template>
  </BittsQuickFilters>
</template>

<script setup lang="ts">
import { BittsInput, BittsQuickFilters } from '@crossbeam/bitts';
import type { FilterProp } from '@crossbeam/bitts/src/BittsQuickFilter/types';

import { useDebounceFn } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import {
  MY_POPULATIONS_FILTER,
  OWNER_FILTER,
  PARTNER_FILTER,
  PARTNER_POPULATIONS_FILTER,
  type PartnerTabFilterValues,
  type PopulationFiltersType,
  TAGS_FILTER,
} from '../composables/usePartnerTabFilters';
import type { PopulationFilterSelectionType } from '../types';

import PartnerTabPopulationFilter from './PartnerTabPopulationFilter.vue';

const { partnerOptions, ownerOptions, tagOptions } = defineProps<{
  partnerOptions: FilterProp[];
  ownerOptions: FilterProp[];
  tagOptions: FilterProp[];
}>();
const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: PartnerTabFilterValues];
  'population-filters-applied': [populationFilters: PopulationFiltersType];
}>();
const CUSTOMIZED = 'customized';

const POPULATION_SIDES = {
  [PARTNER_POPULATIONS_FILTER]: 'partner',
  [MY_POPULATIONS_FILTER]: 'mine',
} as const;

const filterOptions: FilterProp[] = [
  {
    label: 'Partner Populations',
    value: PARTNER_POPULATIONS_FILTER,
    faIcon: ['fak', 'population'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'My Populations',
    value: MY_POPULATIONS_FILTER,
    faIcon: ['fak', 'population'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'Partner',
    value: PARTNER_FILTER,
    faIcon: ['fak', 'partner'],
    datatype: 'text',
    useSearch: true,
    noCustomValue: true,
  },
  {
    label: 'Owner',
    value: OWNER_FILTER,
    faIcon: ['far', 'user'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'Tags',
    value: TAGS_FILTER,
    faIcon: ['far', 'tag'],
    datatype: 'text',
    noCustomValue: true,
  },
];

const partnerOrgIds = computed(() =>
  partnerOptions.map((option) => Number(option.value)),
);

const filterValueOptions = computed<Record<string, FilterProp[]>>(() => ({
  [PARTNER_FILTER]: partnerOptions,
  [OWNER_FILTER]: ownerOptions,
  [TAGS_FILTER]: tagOptions,
}));

const searchValue = ref('');

const selections = ref(emptySelections());

/**
 * Applied as the panel changes rather than when it closes: the chip keeps the
 * same marker while a customized selection is edited, so BittsQuickFilters
 * sees no change to report on close.
 */
watch(selections, (value) => emit('population-filters-applied', { ...value }), {
  deep: 1,
});

const populationChipTexts = computed(() =>
  Object.fromEntries(
    Object.entries(selections.value)
      .filter(([, selection]) => isCustomized(selection))
      .map(([key]) => [key, 'Customized']),
  ),
);

function emptySelection(): PopulationFilterSelectionType {
  return { operator: 'OR', levels: [], items: [] };
}

function emptySelections(): PopulationFiltersType {
  return {
    [PARTNER_POPULATIONS_FILTER]: emptySelection(),
    [MY_POPULATIONS_FILTER]: emptySelection(),
  };
}

function isCustomized({
  operator,
  levels,
  items,
}: PopulationFilterSelectionType) {
  return operator !== 'OR' || !!levels.length || !!items.length;
}

function isPopulationFilter(
  filterId: FilterProp['value'],
): filterId is keyof PopulationFiltersType {
  return filterId in POPULATION_SIDES;
}

/**
 * A quick filter only keeps its chip, and offers to remove it, while it holds
 * a value. The panel's selection is not an option list, so a marker stands in
 * for it.
 */
function updateSelection(
  filterId: FilterProp['value'],
  selection: PopulationFilterSelectionType,
  setFilterValue: (filterId: string, value: string[]) => void,
) {
  if (!isPopulationFilter(filterId)) return;
  selections.value[filterId] = selection;
  setFilterValue(filterId, isCustomized(selection) ? [CUSTOMIZED] : []);
}

function resetSelection(filterId: FilterProp['value']) {
  if (isPopulationFilter(filterId))
    selections.value[filterId] = emptySelection();
}

const debouncedSearch = useDebounceFn((value: string) => {
  emit('search-applied', value);
}, 300);

/**
 * BittsQuickFilters hands back every datatype it supports; the simple filters
 * here only ever carry arrays, so anything else is treated as unset.
 */
function handleUpdateFilters({
  filterValues,
  isReset,
}: {
  filterValues: Record<
    string,
    (string | number)[] | Record<string, unknown> | null
  >;
  isReset?: boolean;
}) {
  if (isReset) selections.value = emptySelections();
  const applied: PartnerTabFilterValues = {};
  Object.entries(filterValues).forEach(([key, value]) => {
    if (Array.isArray(value) && !isPopulationFilter(key)) applied[key] = value;
  });
  emit('filters-applied', applied);
}
</script>
